import React from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';

import useStore from 'Hooks/useStore';
import { DefaultPricingEntity, UserEntity } from 'Models/Entities';
import { defaultPricingKeys } from 'Modules/DefaultPricing/Hooks/defaultPricingKeys';
import { RequestUserTypeButton } from 'Modules/UserTypeRequests/Components/RequestUserTypeButton';
import { defaultPricingToRows } from 'Modules/UserAccount/Pricing/userPricingRows';
import { fetchDefaultPricing } from 'Services/Api/_HumanWritten/DefaultPricingApi';
import { useUserDisplayNamesStore } from 'Global/User/UserDisplayNameStore';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import { SelectInput } from 'Views/Components/New/SelectInput';
import { newConfirmModal } from 'Views/Components/Modal/NewConfirmModal';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import { AccountFormValues } from '../Hooks/useAccountEditSession';

interface UserTypeSectionProps {
	/** The fetched user — ids for the request flow. */
	user: UserEntity;
	form: UseFormReturn<AccountFormValues>;
	mode: EntityFormMode;
	canEdit: boolean;
	/** Refetches the user after a manager accepts a pending request (accept assigns the type). */
	onTypeAssigned?: () => void;
}

/**
 * The customer-type field on the Account tab — the type's home now that it has moved out of the
 * Pricing tab (a type doesn't imply custom pricing). Rendered inside DetailFields via its
 * userTypeSlot so it reads as just another account field: label + value in view mode, a select
 * in edit mode. Only managers can change it (assignment of linked default pricing moved across
 * with the field); staff see the select disabled and use the request flow instead, which is also
 * where the pending state (with cancel/reject) surfaces for everyone.
 */
export default function UserTypeSection({ user, form, mode, canEdit, onTypeAssigned }: UserTypeSectionProps) {
	const { isManager } = useStore();
	const { userDisplayNames } = useUserDisplayNamesStore();
	const queryClient = useQueryClient();

	// Single source of truth: the form's userDisplayNameId (in view mode it still holds the
	// fetched user's type, since the form defaults come from the fetched entity).
	const selectedTypeId = useWatch({ control: form.control, name: 'userDisplayNameId' });
	const currentType = userDisplayNames.find(x => x.id === selectedTypeId);

	if (mode === EntityFormMode.VIEW) {
		return (
			<div className="readonly-field__container" data-test-id="account-user-type-field">
				<p className="readonly-field__header">Customer type</p>
				{/*
					Not readonly-field__body: its scss (2-class specificity) forces a plain row,
					and this body needs to be responsive — value and request button side by side
					and centred on desktop, stacked on mobile.
				*/}
				<div className="flex flex-col items-start gap-2 pb-1 md:flex-row md:items-center md:gap-6">
					<p className="readonly-field__value customer-type-field !m-0">
						{currentType?.displayName ?? 'Standard'}
					</p>
					<RequestUserTypeButton
						userId={user.id}
						currentUserDisplayNameId={user.userDisplayNameId}
						staffView
						onAccepted={onTypeAssigned}
					/>
				</div>
			</div>
		);
	}

	const handleTypeChange = async (option: { value: string; label: string } | null) => {
		const value = option ? userDisplayNames.find(x => x.id === option.value) : undefined;
		if (isNotNullOrUndefined(value)) {
			form.setValue('userDisplayNameId', value.id, { shouldDirty: true });

			// When the chosen type has linked default pricing, offer to apply it to this customer's
			// price rows. The Pricing tab isn't mounted while this runs; its seed effect backfills
			// placeholder rows against these prices when it next mounts, and the rows persist through
			// the form → saveUser path on Save.
			// Manager+ only: pricing is saved solely for managers (saveUser's includePricing), so
			// offering the prompt to a non-manager would populate rows that are silently dropped on
			// save. (The select is already manager-only, so this is a belt-and-suspenders gate.)
			let defaultPricing: DefaultPricingEntity | null = null;
			if (isManager && isNotNullOrUndefined(value.defaultPricingId)) {
				try {
					defaultPricing = await queryClient.fetchQuery({
						queryKey: defaultPricingKeys.detail(value.defaultPricingId),
						queryFn: () => fetchDefaultPricing(value.defaultPricingId!),
					});
				} catch {
					defaultPricing = null;
				}
			}

			if (isNotNullOrUndefined(defaultPricing)) {
				const applyDefault = await newConfirmModal(
					'Apply default pricing?',
					<p>
						The <strong>{value.displayName}</strong> type has default pricing linked. Apply it to this
						customer now? This replaces their current prices and add-on prices.
					</p>,
					{
						confirmText: 'Apply default pricing',
						cancelText: 'Keep current prices',
						resolveOnCancel: true,
					},
				).catch(() => false);

				if (applyDefault) {
					const rows = defaultPricingToRows(defaultPricing);
					form.setValue('prices', rows.prices, { shouldDirty: true });
					form.setValue('addOnsPrices', rows.addOnsPrices, { shouldDirty: true });
				}
			}
		} else {
			form.setValue('userDisplayNameId', null, { shouldDirty: true });
			form.setValue('prices', []);
			form.setValue('addOnsPrices', []);
			form.setValue('additionalInformation', null);
		}
	};

	const options = userDisplayNames.map(x => ({ value: x.id, label: x.displayName }));
	const selectedOption = options.find(o => o.value === selectedTypeId) ?? null;
	const isEditable = isManager && canEdit;

	return (
		<div data-test-id="account-user-type-field">
			<SelectInput
				label="Customer type"
				placeholder="Standard"
				options={options as any}
				value={selectedOption}
				isClearable
				isDisabled={!isEditable}
				onChange={(option: any) => handleTypeChange(option)}
			/>
			{currentType?.description && <p className="!mb-0 mt-1 text-sm text-gray-500">{currentType.description}</p>}
			{!isEditable && (
				<div className="mt-2">
					<RequestUserTypeButton
						userId={user.id}
						currentUserDisplayNameId={user.userDisplayNameId}
						staffView
						onAccepted={onTypeAssigned}
					/>
				</div>
			)}
		</div>
	);
}
